import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ProjectControlsDto, ProjectGovernanceDto } from '@paladin-monorepo/common';
import { monitoringQueryKey } from '@/lib/api/project-monitoring';
import { milestoneDependenciesQueryKey, scheduleQueryKey } from '@/lib/api/project-governance';
import { managementProductsQueryKey, portfolioQueryKey } from '@/lib/api/project-portfolio';

export const governanceQueryKey = (projectId: string) => ['projectGovernance', projectId] as const;
export const controlsQueryKey = (projectId: string) => ['projectControls', projectId] as const;
export const registersQueryKey = (projectId: string) => ['projectRegisters', projectId] as const;
export const changeRequestsQueryKey = (projectId: string) => ['projectChangeRequests', projectId] as const;
export const approvalsInboxQueryKey = ['approvalsInbox'] as const;

/** 409 reasons that have their own message. Other 409 responses are version conflicts. */
const CONFLICT_REASONS = [
  'governance-request-stale',
  'governance-request-missing',
  'idempotency-in-progress',
];

function useGovernanceErrorHandler(projectId: string) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return (error: unknown) => {
    const status = axios.isAxiosError(error) ? error.response?.status : undefined;
    const reason = axios.isAxiosError(error) ? error.response?.data?.reason : undefined;
    if (status === 409) {
      queryClient.invalidateQueries({ queryKey: governanceQueryKey(projectId) });
      queryClient.invalidateQueries({ queryKey: approvalsInboxQueryKey });
      toast.error(
        CONFLICT_REASONS.includes(reason)
          ? t(`workspace.governance.errors.${reason}`)
          : t('workspace.governance.errors.conflict'),
      );
      return;
    }
    if (status === 403) {
      toast.error(t('workspace.governance.errors.forbidden'));
      return;
    }
    toast.error(
      typeof reason === 'string'
        ? t(`workspace.governance.errors.${reason}`, { defaultValue: t('workspace.governance.errors.generic') })
        : t('workspace.governance.errors.generic'),
    );
  };
}

/** Runs a governance command, stores the returned record, and shows a translated error. */
export function useGovernanceMutation<TArgs = void>(
  projectId: string,
  fn: (args: TArgs) => Promise<ProjectGovernanceDto>,
  onDone?: () => void,
) {
  const queryClient = useQueryClient();
  const onError = useGovernanceErrorHandler(projectId);
  return useMutation({
    mutationFn: fn,
    onSuccess: (dto) => {
      queryClient.setQueryData(governanceQueryKey(projectId), dto);
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      queryClient.invalidateQueries({ queryKey: controlsQueryKey(projectId) });
      queryClient.invalidateQueries({ queryKey: registersQueryKey(projectId) });
      queryClient.invalidateQueries({ queryKey: changeRequestsQueryKey(projectId) });
      queryClient.invalidateQueries({ queryKey: approvalsInboxQueryKey });
      queryClient.invalidateQueries({ queryKey: monitoringQueryKey(projectId) });
      queryClient.invalidateQueries({ queryKey: portfolioQueryKey });
      queryClient.invalidateQueries({ queryKey: managementProductsQueryKey(projectId) });
      queryClient.invalidateQueries({ queryKey: scheduleQueryKey(projectId) });
      queryClient.invalidateQueries({ queryKey: milestoneDependenciesQueryKey(projectId) });
      onDone?.();
    },
    onError,
  });
}

/** Runs an operational command (ETC, forecast) that returns the controls view and keeps the version. */
export function useControlsMutation<TArgs = void>(
  projectId: string,
  fn: (args: TArgs) => Promise<ProjectControlsDto>,
  onDone?: () => void,
) {
  const queryClient = useQueryClient();
  const onError = useGovernanceErrorHandler(projectId);
  return useMutation({
    mutationFn: fn,
    onSuccess: (dto) => {
      queryClient.setQueryData(controlsQueryKey(projectId), dto);
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      queryClient.invalidateQueries({ queryKey: registersQueryKey(projectId) });
      queryClient.invalidateQueries({ queryKey: monitoringQueryKey(projectId) });
      queryClient.invalidateQueries({ queryKey: portfolioQueryKey });
      queryClient.invalidateQueries({ queryKey: managementProductsQueryKey(projectId) });
      queryClient.invalidateQueries({ queryKey: scheduleQueryKey(projectId) });
      onDone?.();
    },
    onError,
  });
}

/** Change Requests. An approval also changes the governance baseline. */
export function useChangeRequestsMutation<TArgs = void, TResult = unknown>(
  projectId: string,
  fn: (args: TArgs) => Promise<TResult>,
  onDone?: () => void,
) {
  const queryClient = useQueryClient();
  const onError = useGovernanceErrorHandler(projectId);
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: changeRequestsQueryKey(projectId) });
      queryClient.invalidateQueries({ queryKey: governanceQueryKey(projectId) });
      queryClient.invalidateQueries({ queryKey: controlsQueryKey(projectId) });
      queryClient.invalidateQueries({ queryKey: registersQueryKey(projectId) });
      queryClient.invalidateQueries({ queryKey: approvalsInboxQueryKey });
      queryClient.invalidateQueries({ queryKey: monitoringQueryKey(projectId) });
      queryClient.invalidateQueries({ queryKey: portfolioQueryKey });
      queryClient.invalidateQueries({ queryKey: managementProductsQueryKey(projectId) });
      onDone?.();
    },
    onError,
  });
}

/** Operational register writes. They do not change the governance version. */
export function useRegistersMutation<TArgs = void, TResult = unknown>(
  projectId: string,
  fn: (args: TArgs) => Promise<TResult>,
  onDone?: () => void,
) {
  const queryClient = useQueryClient();
  const onError = useGovernanceErrorHandler(projectId);
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: registersQueryKey(projectId) });
      queryClient.invalidateQueries({ queryKey: controlsQueryKey(projectId) });
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      queryClient.invalidateQueries({ queryKey: monitoringQueryKey(projectId) });
      queryClient.invalidateQueries({ queryKey: portfolioQueryKey });
      queryClient.invalidateQueries({ queryKey: managementProductsQueryKey(projectId) });
      onDone?.();
    },
    onError,
  });
}
